import { AlertCircle, ExternalLink, Users } from "lucide-react";
import { useState, type ReactNode } from "react";

import { CardGrid } from "components/CardGrid";
import { SectionHeader } from "components/SectionHeader";
import { EmptyState, LoadingCards } from "components/states";
import { SECTION_TITLES } from "../../copy/section-titles";
import { Card } from "components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "components/ui/sheet";

import { safeHttpUrl } from "./external-url";
import type { Partner } from "./schemas";
import { usePartners } from "./use-event-content";

export function PartnersSection({ eventId }: { eventId: string }) {
  const partners = usePartners(eventId);
  const [opened, setOpened] = useState<Partner | null>(null);

  const count = partners.status === "success" ? partners.data.length : 0;
  const head = (
    <SectionHeader
      className="mb-6"
      kicker={SECTION_TITLES.partners.kicker}
      title={SECTION_TITLES.partners.title}
      subline={
        count > 0
          ? `${count} ${count === 1 ? "partner. Tap it" : "partners. Tap one"} to read more.`
          : undefined
      }
    />
  );

  if (partners.status === "loading")
    return (
      <Frame>
        {head}
        <LoadingCards cards={2} mode="grid" />
      </Frame>
    );
  if (partners.status === "error")
    return (
      <Frame>
        {head}
        <EmptyState icon={AlertCircle} title="Could not load partners">
          {partners.error}
        </EmptyState>
      </Frame>
    );
  if (partners.data.length === 0)
    return (
      <Frame>
        {head}
        <EmptyState icon={Users} title="No partners yet">
          Partners will appear here once added.
        </EmptyState>
      </Frame>
    );

  return (
    <Frame>
      {head}
      <CardGrid testId="partners-content" className="gap-3">
        {partners.data.map((p) => (
          <PartnerCard key={p.id} partner={p} onOpen={() => setOpened(p)} />
        ))}
      </CardGrid>
      {/* Outside the grid: the sheet is not a tile, and a child that renders
          nothing most of the time is a grid cell waiting to appear. */}
      <PartnerSheet
        partner={opened}
        onOpenChange={(open) => {
          if (!open) setOpened(null);
        }}
      />
    </Frame>
  );
}

/** The tab's column, capped on a desktop so the tiles keep their shape. */
function Frame({ children }: { children: ReactNode }) {
  return <div className="mx-auto w-full max-w-5xl">{children}</div>;
}

/**
 * The website link stays a sibling of the opening button rather than inside
 * it: an anchor nested in a button is invalid, and the two do different things.
 */
function PartnerCard({
  partner,
  onOpen,
}: {
  partner: Partner;
  onOpen: () => void;
}) {
  const websiteUrl = safeHttpUrl(partner.websiteUrl);
  return (
    <Card className="flex h-full flex-col items-center rounded-[22px] border-border bg-card p-3.5 text-center">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`${partner.name}, view details`}
        // `w-full min-w-0` for the same reason the speaker card carries it: a
        // button is an inline-block sized to max-content, so one unbreakable
        // string in a partner name widens the grid track past the viewport.
        className="flex min-h-11 w-full min-w-0 flex-col items-center rounded-2xl p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        data-testid={`partner-card-${partner.id}`}
      >
        {partner.logo && (
          <img
            src={partner.logo}
            alt={partner.name}
            className="mb-3 h-16 w-auto max-w-full object-contain"
          />
        )}
        <p className="font-display text-[1.3125rem] leading-[1.12] text-foreground">
          {partner.name}
        </p>
        {partner.description && (
          <p className="mt-1 line-clamp-3 text-sm text-muted-foreground wrap-anywhere">
            {partner.description}
          </p>
        )}
      </button>
      {websiteUrl && (
        <a
          href={websiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex min-h-11 items-center justify-center gap-1.5 px-3 text-sm font-semibold text-lit hover:underline"
          data-testid={`partner-website-${partner.id}`}
        >
          Visit website
          <ExternalLink className="h-3.5 w-3.5 shrink-0" />
        </a>
      )}
    </Card>
  );
}

function PartnerSheet({
  partner,
  onOpenChange,
}: {
  partner: Partner | null;
  onOpenChange: (open: boolean) => void;
}) {
  const websiteUrl = safeHttpUrl(partner?.websiteUrl);
  return (
    <Sheet open={partner !== null} onOpenChange={onOpenChange}>
      <SheetContent>
        {partner && (
          <div className="space-y-6 pt-6">
            {/* Everything in the sheet lines up on the left: a centred logo and
                name above a left-aligned description looked like two layouts. */}
            <SheetHeader className="items-start text-left">
              {partner.logo && (
                <img
                  src={partner.logo}
                  alt=""
                  className="h-20 w-auto object-contain"
                />
              )}
              <SheetTitle
                data-testid="partner-sheet-name"
                className="wrap-anywhere"
              >
                {partner.name}
              </SheetTitle>
            </SheetHeader>
            {partner.description && (
              <p
                className="whitespace-pre-line text-sm leading-relaxed text-muted-foreground"
                data-testid="partner-sheet-description"
              >
                {partner.description}
              </p>
            )}
            {websiteUrl && (
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-11 items-center gap-1.5 rounded-sm text-sm font-medium text-lit hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                data-testid={`partner-sheet-website-${partner.id}`}
              >
                Visit website
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
